/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import {useIntl} from 'react-intl'

// Components
import {
    Alert,
    Text,
    AlertIcon,
    // Hooks
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'

/**
 * Displays an informational banner indicating that the user is currently browsing in offline mode.
 *
 * Utilizes internationalization to provide a localized message and applies custom styles via the `useStyleConfig` hook.
 * The banner is rendered using Chakra UI's `Alert`, `AlertIcon`, and `Text` components, ensuring consistent styling and accessibility.
 *
 * @component
 * @returns {JSX.Element} A styled alert banner with an icon and a localized offline mode message.
 */
const OfflineBanner = () => {
    const intl = useIntl()
    const style = useStyleConfig('OfflineBanner')

    return (
        <Alert status="info" {...style.container}>
            <AlertIcon {...style.icon} />
            <Text {...style.message}>
                {intl.formatMessage({
                    id: 'offline_banner.description.browsing_offline_mode',
                    defaultMessage: "You're currently browsing in offline mode"
                })}
            </Text>
        </Alert>
    )
}

export default OfflineBanner
